// frontend/src/pages/PayrollGuide/components/StepSection.tsx
// One expandable setup-step card (rendered 5 times in the main page).

import React from "react";
import { ChevronRight } from "lucide-react";
import type { Step } from "../types";
import type { SizeConfig } from "../styles";
import { C, FONT } from "../styles";

interface StepSectionProps {
  step: Step;
  sz: SizeConfig;
  /** Called when the step's CTA button is clicked. Parent decides where to navigate. */
  onCtaClick: () => void;
}

export default function StepSection({
  step,
  sz,
  onCtaClick,
}: StepSectionProps): React.ReactElement {
  return (
    <section
      style={{
        background: "#fff",
        border: "1px solid " + C.line,
        borderRadius: 14,
        padding: sz.secPad,
        marginBottom: 14,
        boxShadow: "0 1px 2px rgba(16,26,43,0.04)",
      }}
    >
      {/* Header: number + title + time */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
        <div
          style={{
            width: sz.secNum,
            height: sz.secNum,
            borderRadius: sz.secNumRadius,
            background: C.tealSoft,
            color: C.tealInk,
            display: "grid",
            placeItems: "center",
            flex: "0 0 " + sz.secNum + "px",
            fontWeight: 700,
            fontSize: sz.secNum >= 32 ? 14 : 12.5,
          }}
        >
          {step.num}
        </div>
        <h2
          style={{
            fontSize: sz.secH2,
            fontWeight: 600,
            color: C.ink,
            letterSpacing: "-0.01em",
            flex: 1,
            minWidth: 0,
          }}
        >
          {step.title}
        </h2>
        <span
          style={{
            marginLeft: "auto",
            fontSize: sz.secTime,
            fontWeight: 600,
            color: C.muted,
            background: C.lineSoft,
            padding: "3px 9px",
            borderRadius: 20,
          }}
        >
          {step.time}
        </span>
      </div>

      {/* Body: intro + why callout + extra + CTA */}
      <div style={{ fontSize: sz.secBody, color: C.text, lineHeight: sz.secLineH }}>
        <p style={{ marginBottom: 12 }}>{step.intro}</p>

        <div
          style={{
            background: C.tealSoft,
            borderLeft: "3px solid " + C.teal,
            padding: sz.whyPad,
            margin: "12px 0",
            borderRadius: "0 8px 8px 0",
            fontSize: sz.whyFont,
          }}
        >
          <strong
            style={{
              display: "block",
              color: C.tealInk,
              fontSize: sz.whyLabel,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              marginBottom: 5,
            }}
          >
            Why this matters
          </strong>
          {step.why}
        </div>

        <p style={{ marginBottom: 12 }}>{step.extra}</p>

        <button
          onClick={onCtaClick}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            background: C.teal,
            color: "#fff",
            border: "none",
            borderRadius: 9,
            padding: sz.btnPad,
            fontWeight: 600,
            fontSize: sz.btnFont,
            cursor: "pointer",
            marginTop: 10,
            fontFamily: FONT,
            boxShadow: "0 1px 3px rgba(21,160,140,0.18)",
          }}
        >
          {step.cta} <ChevronRight size={14} />
        </button>
      </div>
    </section>
  );
}